import { AcModel } from './ac.model';
import { AcStatusSettingsExerciseType, ActiveTiming, ExerciseStatus, RatingstartType } from './acstatus/ac-status.type';
import { ExerciseModel as CoreExerciseModel } from 'projects/core/src/lib/models/exercise.model';
import { GroupModel } from './group.model';
import { SlotModel } from './slot.model';
import { ElementModel } from './element.model';
import { HelperService } from '../services/helper.service';

export interface GapType {
  amount: number,
  type: string
}

export class ExerciseModel extends CoreExerciseModel {

  // runner only
  ac: AcModel = null;
  slots: Array<SlotModel> = [];
  status: ExerciseStatus = null;
  settings: AcStatusSettingsExerciseType = null;
  groups: Array<GroupModel> = [];
  isOpen = false;
  isActive = false;
  allCandidatesLinked = false;
  duration = 0;

  public setStatusSettings(settings: AcStatusSettingsExerciseType): void {
    this.settings = settings;
    this.setSlotTimes();
    this.initGroups();
    this.setPermissions();
  }

  public hasSettings(): boolean {
    return this.settings !== null;
  }

  public isSingleAc(): boolean {
    return this.ac?.isSingle() || false;
  }

  public isGroupAc(): boolean {
    return this.ac?.isGroup() || false;
  }

  /**
   * Gets the maximum number of candidates lying in a row
   *
   * @returns number
   */
  public getMaxCandidateSequence(): number {
    let result = 1;
    if (this.isIndividual()) {
      this.groups.forEach((group: GroupModel) => {
        result = Math.max(result, group.candidates?.length);
      });
    }
    return result;
  }

  /**
   * Gets the full duration for a group exercise
   *
   * @returns number
   */
  public getGroupDuration(): number {
    const candidates = this.getMaxCandidateSequence();
    if (this.isIndividual() && candidates > 1) {
      const gap = this.getExerciseGap();
      return (this.duration * candidates) - (gap * (candidates - 1));
    } else {
      return this.duration;
    }
  }

  /**
   * Gets the full duration for a list of slots
   *
   * @param slots Array<SlotModel>
   * @returns number
   */
  public getSlotlistDuration(slots: Array<SlotModel>, authType: string | null = null): number {
    let duration = 0;
    slots.forEach((slot: SlotModel, index: number) => {
      duration += slot.duration;
      if (slot.rating_start === RatingstartType.End && authType !== 'candidate') {
        duration += slot.rating_duration;
      }
    });
    return duration;
  }

  public setBusyFlags(timings: Array<ActiveTiming>): void {
    const slotIds = this.slots.map(slot => slot.id);
    const relevantTimings = timings.filter(timing => slotIds.includes(timing.slot?.id));
    this.isActive = relevantTimings.length > 0;
    this.groups.forEach((group: GroupModel) => {
      group.setBusyFlags(relevantTimings, this.ac);
    });
  }

  /**
   * Adjusts slot times from settings
   */
  private setSlotTimes(): void {
    // set durations from settings, if present
    if (this.settings?.slots) {
      Object.keys(this.settings.slots).forEach((key: string) => {
        const duration = this.settings.slots[key].duration || null;
        const rating_duration = this.settings.slots[key].rating_duration || null;
        const slot = this.slots.find(actual => actual.id === parseInt(key, 10)) || null;
        if (slot !== null) {
          if (duration !== null && !isNaN(duration)) {
            slot.duration = duration;
          }
          if (slot.rating_start === RatingstartType.End && rating_duration !== null && !isNaN(rating_duration)) {
            slot.rating_duration = rating_duration;
          }
        }
      });
    }
    // calc exercise duration
    this.duration = this.getSlotlistDuration(this.slots);
  }

  /**
   * Gets the amount of minutes what an exercise can overlap with itself
   *
   * @returns number
   */
  public getExerciseGap(): number {
    if (this.ac?.isGroup() && this.hasOverlapping()) {
      return HelperService.getSlotListGap(this.slots);
    }
    return 0;
  }

  private initGroups(): void {
    let groupCount = 0;
    this.groups = [];
    if (this.settings?.groups) {
      Object.keys(this.settings?.groups).forEach((groupHash: string) => {
        const group = new GroupModel().deserialize(this.settings?.groups[groupHash]);
        group.exercise = this;
        group.hash = groupHash;
        group.setParticipants(this.settings, this.ac);
        this.groups.push(group);
        groupCount += group.candidates?.length || 0;
      });
    }
    const candidateCount = this.ac?.candidates?.length || 0;
    this.allCandidatesLinked = groupCount === candidateCount;
  }

  private setPermissions(): void {
    this.slots.forEach((slot: SlotModel) => {
      if (this.settings.slots && this.settings.slots[slot.id]) {
        slot.elements.forEach((element: ElementModel) => {
          if (this.settings.slots[slot.id].permissions && this.settings.slots[slot.id].permissions[element.id]) {
            element.permissions = this.settings.slots[slot.id].permissions[element.id];
          }
        });
      }
    });
  }

}
